import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';

// A first-run guided tour that spotlights the real elements on the screen behind
// it — so a brand-new teacher learns where to tap instead of guessing. Each step
// points at a live element by its data-tour attribute (or is centered, for the
// intro/outro). The spotlight is one positioned box with a huge box-shadow, which
// dims everything outside it in a single element (no SVG mask needed).
//
// It is deliberately self-contained and presentational: the caller owns when to
// show it (first login) and how to remember it's been seen. Nothing here touches
// storage, so it can be dropped onto any screen and replayed on demand.

export interface TourStep {
  // CSS selector of the element to spotlight, or null for a centered card
  // (used for the welcome + "you're all set" steps).
  selector: string | null;
  emoji?: string;
  title: string;
  body: string;
}

interface Props {
  steps: TourStep[];
  onClose: () => void; // called on Finish or Skip — caller records "seen" + cleans up
}

interface Rect { top: number; left: number; width: number; height: number; }

const PAD = 8; // breathing room around the highlighted element

export default function GuidedTour({ steps, onClose }: Props) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const step = steps[i];
  const isLast = i === steps.length - 1;

  // Measure the current target. Centered steps (no selector) clear the rect so we
  // render the card in the middle with a plain dim behind it.
  const measure = useCallback(() => {
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // On each step: bring the target into view, then measure once it's settled.
  useLayoutEffect(() => {
    if (step?.selector) {
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const t = setTimeout(measure, step?.selector ? 320 : 0);
    return () => clearTimeout(t);
  }, [step, measure]);

  // Keep the spotlight glued to the target if the layout shifts.
  useEffect(() => {
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [measure]);

  // Keyboard: arrows to move, Esc to skip.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { isLast ? onClose() : setI(v => v + 1); }
      else if (e.key === 'ArrowLeft') setI(v => Math.max(0, v - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLast, onClose]);

  if (!step) return null;

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;

  // Spotlight geometry (only when we have a target).
  const spot = rect ? {
    top: rect.top - PAD,
    left: rect.left - PAD,
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  } : null;

  // Card placement: below the target if there's room, otherwise above; centered
  // steps sit in the middle. Horizontal position is clamped into the viewport.
  const CARD_W = Math.min(340, vw - 32);
  let cardStyle: React.CSSProperties;
  if (!spot) {
    cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: CARD_W };
  } else {
    const below = spot.top + spot.height + 14;
    const placeBelow = below < vh - 220;
    const top = placeBelow ? below : Math.max(16, spot.top - 14 - 210);
    let left = spot.left + spot.width / 2 - CARD_W / 2;
    left = Math.max(16, Math.min(left, vw - CARD_W - 16));
    cardStyle = { top, left, width: CARD_W };
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      {/* Click-blocker so the app behind the tour isn't interactive mid-tour. A
          plain dim is used for centered steps; the spotlight provides the dim for
          targeted steps via its box-shadow. */}
      <div
        onClick={() => { /* swallow clicks on the backdrop */ }}
        style={{ position: 'absolute', inset: 0, background: spot ? 'transparent' : 'rgba(13,61,61,0.72)', cursor: 'default' }}
      />

      {/* Spotlight: one box whose enormous box-shadow darkens everything else. */}
      {spot && (
        <div style={{
          position: 'fixed',
          top: spot.top, left: spot.left, width: spot.width, height: spot.height,
          borderRadius: 12,
          boxShadow: '0 0 0 9999px rgba(13,61,61,0.72)',
          border: '2px solid #fff',
          pointerEvents: 'none',
          transition: 'all 0.3s ease',
        }} />
      )}

      {/* Step card */}
      <div className="animate-in" style={{
        position: 'fixed', ...cardStyle,
        background: '#fff', borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
        padding: '20px', boxSizing: 'border-box',
      }}>
        {step.emoji && <div style={{ fontSize: 30, marginBottom: 8 }}>{step.emoji}</div>}
        <p style={{ fontWeight: 800, fontSize: 17, color: 'var(--teal)', marginBottom: 6 }}>{step.title}</p>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--charcoal)' }}>{step.body}</p>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '16px 0 14px' }}>
          {steps.map((_, idx) => (
            <div key={idx} style={{ width: idx === i ? 20 : 7, height: 7, borderRadius: 4, background: idx === i ? 'var(--teal)' : '#d8d2ca', transition: '0.2s' }} />
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {i > 0 && (
            <button onClick={() => setI(v => v - 1)} className="btn-secondary" style={{ flex: '0 0 auto', padding: '10px 16px', fontSize: 14 }}>
              Back
            </button>
          )}
          <button onClick={() => isLast ? onClose() : setI(v => v + 1)} className="btn-primary" style={{ flex: 1, padding: '10px 16px', fontSize: 14 }}>
            {isLast ? "Let's go" : 'Next'}
          </button>
        </div>

        {!isLast && (
          <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, marginTop: 10, cursor: 'pointer' }}>
            Skip tour
          </button>
        )}
      </div>
    </div>
  );
}
